import { PERIODS } from '@/utils/filter.constants'
import datetimeUtils from '@/utils/datetime.utils'
import values from 'ramda/src/values'

class AssistanceResolutionsUtils {
  /**
   * Creates a class for managing assistance resolution area chart with its own state keeping track
   * of currently focused period
   * @param {number} from - an initial starting(first) unix timestamp of zoomed period
   * @param {number} to - an initial ending(last) unix timestamp of zoomed period
   */
  constructor(from, to) {
    this.from = from
    this.to = to
  }

  getChangedTimestamps({ valueFrom, valueTo, name, forward }) {
    const changeOperator = forward ? 'add' : 'subtract'
    const { amount, unit, milliseconds } = PERIODS[name]

    const newValues = { newValueFrom: datetimeUtils.mutateDate(valueFrom, unit, amount, changeOperator).valueOf() }
    // months and higher periods don't have a static number of milliseconds
    if (milliseconds >= PERIODS.month.milliseconds) {
      newValues.newValueTo = datetimeUtils.mutateBorderDate(valueTo, unit, amount, changeOperator, 'endOf').valueOf()
    } else {
      newValues.newValueTo = datetimeUtils.mutateDate(valueTo, unit, amount, changeOperator).valueOf()
    }

    return newValues
  }

  getPeriodProps(initFrom) {
    const { name, formatter, amount, unit } = this._getPeriodProps(this.from, this.to)
    const stepProps = { formatter, name }

    if (unit === 'minute') {
      stepProps.valueFrom = datetimeUtils.roundToMinutes(initFrom, amount).valueOf()
      stepProps.valueTo = datetimeUtils.roundToMinutes(initFrom, amount).add(amount, unit).valueOf()
    } else if (unit === 'hour') {
      stepProps.valueFrom = datetimeUtils.toBorderMoment(initFrom, unit).valueOf()
      stepProps.valueTo = datetimeUtils.toBorderMoment(initFrom, unit).add(amount, unit).valueOf()
    } else if (amount === 1) {
      stepProps.valueFrom = datetimeUtils.toBorderMoment(initFrom, unit).valueOf()
      stepProps.valueTo = datetimeUtils.toBorderMoment(initFrom, unit, 'endOf').valueOf()
    } else {
      stepProps.valueFrom = datetimeUtils.toBorderMoment(initFrom, unit).valueOf()
      stepProps.valueTo = datetimeUtils.mutateBorderDate(initFrom, unit, amount - 1, 'add', 'endOf').valueOf()
    }

    return stepProps
  }

  _getPeriodProps(from, to) {
    const minDataPointsCount = 15
    const range = to - from
    const periods = values(PERIODS)
    const period = periods.find(({ milliseconds }) => range >= milliseconds * minDataPointsCount)
    return period || PERIODS.tenMinutes
  }

  getPeriodLabel({ name, valueFrom, valueTo }, isRange = false) {
    const period = PERIODS[name]
    const momentFrom = datetimeUtils.toMoment(valueFrom)
    const momentTo = datetimeUtils.toMoment(valueTo)

    if (!isRange || [PERIODS.year.name, PERIODS.month.name, PERIODS.day.name].includes(name)) {
      return momentFrom.format(period.format)
    } else if (period.milliseconds <= PERIODS.twelveHours.milliseconds) {
      const fromFormat = momentFrom.date() === momentTo.date() ? 'HH:mm' : 'HH:mm, MMM DD'
      return `${momentFrom.format(fromFormat)} - ${momentTo.format('HH:mm, MMM DD')}`
    } else {
      return `${ momentFrom.format(period.format)} - ${momentTo.format(period.format)}`
    }
  }

  getTimeGranularityParams(valueFrom = this.from, valueTo = this.to, filterValueFrom = null, filterValueTo = null) {
    const preloadedPeriod = (valueTo - valueFrom) * 2
    const { milliseconds } = this._getPeriodProps(valueFrom, valueTo)

    this.from = valueFrom
    this.to = valueTo
    const granularityParams = { timeGranularity: Math.round(milliseconds / 60000) }

    // changing base filter dates should always return granularity information,
    // and it does not require additional params filterValueFrom and filterValueTo
    if (!filterValueFrom && !filterValueTo) return granularityParams

    const dateFrom = filterValueFrom !== valueFrom ?
      datetimeUtils.mutateDate(valueFrom, 'milliseconds', preloadedPeriod, 'subtract').format() :
      datetimeUtils.toMoment(valueFrom).format()
    const dateTo = filterValueTo !== valueTo ?
      datetimeUtils.mutateDate(valueTo, 'milliseconds', preloadedPeriod).format() :
      datetimeUtils.toMoment(valueTo).format()

    return { ...granularityParams, dateFrom, dateTo }
  }
}

export default AssistanceResolutionsUtils
